import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const roleBadge = {
  admin: "bg-[#0A2540] text-white border-[#0A2540]",
  staff: "bg-indigo-100 text-indigo-700 border-indigo-200",
  client: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Team() {
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const roleMutation = useMutation({
    mutationFn: async ({ userId, role }) => (await api.put(`/users/${userId}/role`, { role })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Role updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update role"),
  });

  return (
    <div data-testid="team-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#0F172A]">Team &amp; Access</h1>
        <p className="text-slate-500 mt-1">Set roles for everyone who has signed in. Admins & staff manage the portal; clients only see their own rentals.</p>
      </div>

      <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Current Role</TableHead>
                <TableHead className="w-48">Change Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#0A2540]" /></TableCell></TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="text-xs bg-slate-200">{u.name?.slice(0, 2)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-[#0F172A]">{u.name}{u.user_id === me?.user_id ? " (you)" : ""}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className={`capitalize ${roleBadge[u.role]}`}>{u.role}</Badge></TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(role) => roleMutation.mutate({ userId: u.user_id, role })} disabled={u.user_id === me?.user_id}>
                        <SelectTrigger data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
